import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  AgentAction,
  AgentFeedbackRating,
  AgentKey,
  AgentScope,
  AgentSelection,
  AgentStreamEvent,
} from '../../api/siftApiClient';
import { presetPrompts } from './presetPrompts';
import { loadConversationId, newConversationId, saveConversationId, storageKey } from './conversationId';
import { useMediaQuery, PHONE_QUERY } from '../../hooks/useMediaQuery';
import './AgentChatWidget.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text: string;
  streaming?: boolean;
  error?: boolean;
  /** For role==='tool': the tool names being run, used to render a status chip. */
  tools?: string[];
  /** For role==='tool': true once the tool step has completed (chip stops spinning). */
  done?: boolean;
  /** Server-issued id for this assistant turn, used to attach passive good/bad feedback. */
  turnId?: string;
  /** The user's passive rating on this response, if any. */
  feedback?: AgentFeedbackRating | null;
  /** For failed turns: the prompt to resend, so a transient failure is one click to recover. */
  retryPrompt?: string;
}

const TOOL_LABELS: Record<string, string> = {
  list_dealers: 'Scanning dealers',
  dealer_detail: 'Reading dealer detail',
  dealer_vins: 'Loading dealer inventory',
  inspection_call_list: 'Building the call list',
  vehicle_detail: 'Loading vehicle detail',
  vin_placement: 'Finding where it trades',
  optimize_market: 'Running OPTIC',
  remember_preference: 'Saving that for next time',
  forget_preference: 'Forgetting that',
};
const toolLabel = (name: string) => TOOL_LABELS[name] ?? name.replace(/_/g, ' ');

// Marker the agent emits to separate the always-visible digestible summary from the deeper
// breakdown/reasoning, which the UI collapses behind a toggle. Matches a line like "---DETAILS---"
// (case-insensitive, tolerant of the exact dash count and optional "& reasoning" suffix).
const DETAILS_MARKER = /\n*-{2,}\s*details(?:\s*&\s*reasoning)?\s*-{2,}\n*/i;

/** Split assistant text into the up-front summary and the optional collapsible details section. */
function splitAssistantText(text: string): { summary: string; details: string | null } {
  const parts = text.split(DETAILS_MARKER);
  if (parts.length < 2) return { summary: text.trim(), details: null };
  const summary = parts[0].trim();
  const details = parts.slice(1).join('\n').trim();
  return { summary, details: details || null };
}

/** Turn a `sift:dealer/<id>`, `sift:vin/<VIN>` or `sift:client/<id>` markdown link into a UI
 *  navigation directive. */
function siftLinkAction(href: string | undefined): AgentAction | null {
  if (!href) return null;
  const match = /^sift:(dealer|vin|client)\/(.+)$/i.exec(href.trim());
  if (!match) return null;
  const value = decodeURIComponent(match[2]).trim();
  if (!value) return null;
  const kind = match[1].toLowerCase();
  if (kind === 'client') return { type: 'navigate_client', client_id: value };
  return kind === 'dealer'
    ? { type: 'navigate_dealer', seller_id: value }
    : { type: 'navigate_vin', vin: value.toUpperCase() };
}

// react-markdown's default sanitizer allows only http/https/mailto/tel, so it silently blanks our
// internal sift: hrefs — which made every entity link fall through to a plain new-tab anchor.
const agentUrlTransform = (url: string) =>
  url.startsWith('sift:') ? url : defaultUrlTransform(url);

/** Assistant bubble body: digestible summary, plus an expandable details/reasoning section. */
const AssistantMessage: React.FC<{
  text: string;
  streaming?: boolean;
  onEntityClick: (action: AgentAction) => void;
}> = ({ text, streaming, onEntityClick }) => {
  const [expanded, setExpanded] = useState(false);
  const { summary, details } = splitAssistantText(text);
  // Dealers and VINs the agent names are rendered as sift: links so the rep can jump straight to
  // them instead of hunting the carousel for each row of a ranked list.
  const markdownComponents = React.useMemo(
    () => ({
      a: ({ href, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        const action = siftLinkAction(href);
        if (!action) {
          return (
            <a href={href} target="_blank" rel="noreferrer noopener" {...rest}>
              {children}
            </a>
          );
        }
        return (
          <button
            type="button"
            className="agent-entity-link"
            onClick={() => onEntityClick(action)}
          >
            {children}
          </button>
        );
      },
    }),
    [onEntityClick],
  );
  return (
    <div className="agent-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={agentUrlTransform} components={markdownComponents}>{summary}</ReactMarkdown>
      {details && (
        <div className="agent-details">
          <button
            type="button"
            className="agent-details-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <span className={`agent-details-caret${expanded ? ' open' : ''}`} aria-hidden="true">▸</span>
            {expanded ? 'Hide details & reasoning' : 'Show details & reasoning'}
          </button>
          {expanded && (
            <div className="agent-details-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={agentUrlTransform} components={markdownComponents}>{details}</ReactMarkdown>
            </div>
          )}
        </div>
      )}
      {streaming && (
        <span className="agent-stream-cursor" aria-hidden="true">
          <i />
        </span>
      )}
    </div>
  );
};

/** Passive good/bad feedback controls for one assistant response. Optional — never required. */
const FeedbackButtons: React.FC<{
  value: AgentFeedbackRating | null | undefined;
  onRate: (rating: AgentFeedbackRating) => void;
}> = ({ value, onRate }) => (
  <div className="agent-feedback" role="group" aria-label="Rate this response">
    <button
      type="button"
      className={`agent-feedback-btn agent-feedback-good${value === 'good' ? ' active' : ''}`}
      onClick={() => onRate('good')}
      aria-pressed={value === 'good'}
      aria-label="Good response"
      title="Good response"
    >
      ✓
    </button>
    <button
      type="button"
      className={`agent-feedback-btn agent-feedback-bad${value === 'bad' ? ' active' : ''}`}
      onClick={() => onRate('bad')}
      aria-pressed={value === 'bad'}
      aria-label="Bad response"
      title="Bad response"
    >
      ✗
    </button>
  </div>
);

/** A single clickable follow-up chip (agent-generated or from the welcome card). */
interface Suggestion {
  label: string;
  prompt: string;
}

/**
 * How each board filter is named in the status line under the header.
 *
 * A key absent from here still shows, humanised from its own name — the previous version listed
 * the Client View keys and nothing else, so every Sales View filter was invisible and the banner
 * read "No selection" no matter what the rep had filtered.
 */
const SCOPE_TAG_LABELS: Record<string, string> = {
  // Client View
  managers: 'Mgr',
  market_centers: 'MC',
  rds_territories: 'Terr',
  cse_reps: 'CSE',
  metric_grain: 'Tiers',
  seller_names: 'Seller',
  tiers: 'Tier',
  priorities: 'Priority',
  vin_statuses: 'VIN status',
  // Sales View
  cse_manager: 'Mgr',
  cse_director: 'Dir',
  cse_territory: 'Terr',
  cse_territory_owner: 'Rep',
  market_center: 'MC',
  dealer_group: 'Group',
  tier: 'Tier',
  band: 'Band',
  engagement_band: 'Contact',
  reason_group: 'Reason',
  state: 'State',
  city: 'City',
  client_search: 'Search',
  client_token: 'Client',
  went_dark: 'Went dark',
  target_missing: 'No target',
};

/** A plan change Mia has offered, and how far the rep has got with it. */
interface Proposal {
  action: AgentAction;
  state: 'offered' | 'saving' | 'done' | 'failed';
  error?: string;
}

/** What confirming this proposal will actually do, in the rep's words.
 *
 *  DERIVED, never taken from the model: a button whose text came from the same place as the
 *  request would be free to say "add" over an action that drops. */
const proposalLabel = (a: AgentAction): string => {
  const who = a.client_name || `client ${a.client_id}`;
  const mode = a.contact_mode === 'CALL' ? 'a call' : 'a visit';
  switch (a.action) {
    case 'added':
      return `Add ${who} as ${mode}`;
    case 'dropped':
      return `Drop ${who} from this week`;
    case 'moved':
      return `Move ${who} to trip ${a.to_trip}`;
    case 'reordered':
      return `Make ${who} stop ${a.to_stop}`;
    case 'anchored':
      return `Build the trip around ${who}`;
    case 'mode_changed':
      return `Change ${who} to ${mode}`;
    default:
      return `Update ${who}`;
  }
};
/** Client-computed opening snapshot the welcome card renders (counts + quick starts). */
export interface AgentBriefing {
  /** True once the board has resolved its scope and dealer data is available. */
  ready: boolean;
  dealerCount: number;
  priorities: { key: string; label: string; units: number }[];
  tiers: { key: string; label: string; dealers: number }[];
  callListBands: { key: string; label: string; clients: number }[];
  topDealers: { sellerId: string; name: string; bucketLabel: string; count: number }[];
  /** Set when a dealer is focused on the board, so the welcome card opens dealer-scoped. */
  selectedDealer?: { sellerId: string; name: string };
  /** Set when a VIN pop-out is open, so the welcome card opens vehicle-scoped. */
  selectedVehicle?: { vin: string; label: string };
}


interface AgentChatWidgetProps {
  /**
   * Which Mia this is. Both are Mia to the user; the key selects the tool surface and brief
   * server-side, and namespaces this widget's local state so the two surfaces never share a
   * conversation thread — which would blend the two books' archived transcripts.
   */
  agentKey?: AgentKey;
  /** Current dashboard selection, forwarded to the agent each turn. */
  selection: AgentSelection;
  /** Active board scope, forwarded so the agent ranks the same set the user sees. */
  scope: AgentScope;
  /**
   * True once the board has resolved the user's role into its filters. The opener waits
   * for this so its first lookup is scoped, not global.
   */
  scopeReady: boolean;
  /**
   * What the selection is CALLED, for the status line. `selection` carries ids because that is
   * what the tools need; a rep should not have to recognise their client by id.
   */
  selectionName?: string;
  /** Client-computed snapshot used to render the curated Client View welcome card. */
  briefing?: AgentBriefing;
  /**
   * Welcome card for a surface that is not Client View. Supplied instead of `briefing`: the
   * Client View card is built out of dealer/VIN/priority-bucket vocabulary that means nothing on
   * another board, and a generic card would be worse on both. Receives the widget's own send
   * handler so its quick-start chips run a real turn.
   */
  welcomeCard?: (send: (prompt: string) => void, busy: boolean) => React.ReactNode;
  /** Apply UI action directives the agent returns (navigate, filter, etc.). */
  onActions: (actions: AgentAction[]) => void;
  /**
   * Commit one `propose_plan_edit` the rep pressed. The widget renders the button and knows
   * nothing about what confirming means -- the surface that owns the plan does the write, so this
   * stays a chat widget rather than growing a second copy of the plan API.
   *
   * Omitted on surfaces with no plan, where proposals are then not rendered at all.
   */
  onConfirmProposal?: (action: AgentAction, conversationId: string) => Promise<void>;
  /**
   * A turn already run elsewhere in this agent's thread (the drawer's contact recap). Shown as a
   * user + assistant pair and the widget opens; each new object is a new handoff.
   */
  handoff?: AgentHandoff | null;
}

export interface AgentHandoff {
  prompt: string;
  reply: string;
}

const EMPTY_BRIEFING: AgentBriefing = {
  ready: false,
  dealerCount: 0,
  priorities: [],
  tiers: [],
  callListBands: [],
  topDealers: [],
};

// Persisted widget state is namespaced by agent key (see storageKey). Sharing the conversation id in
// particular would blend the two books' archived transcripts, which is the exact thing
// run_meta.agent_key exists to prevent.
const SIZE_KEY = 'sift_agent_widget_size';
const OPEN_KEY = 'sift_agent_widget_open';
const MIN_W = 320;
const MIN_H = 360;
// Abort a turn if no agent event (delta/tool/actions) arrives for this long. SSE heartbeats keep the
// HTTP connection alive even when the backend is stuck, so we watchdog on real events, not bytes.
const TURN_IDLE_TIMEOUT_MS = 45000;

// Bound a size to the current viewport. The panel is anchored bottom-right and grows up/left, so a
// width/height larger than the viewport pushes the top-left resize handle off-screen. Re-clamping on
// load and on window resize (zoom changes fire 'resize') keeps the handle reachable.
function clampSize(s: { width: number; height: number }): { width: number; height: number } {
  const maxW = Math.min(window.innerWidth - 40, 720);
  const maxH = Math.min(window.innerHeight - 40, 900);
  return {
    width: Math.max(MIN_W, Math.min(maxW, s.width)),
    height: Math.max(MIN_H, Math.min(maxH, s.height)),
  };
}

function loadSize(agentKey: AgentKey): { width: number; height: number } {
  try {
    const raw = localStorage.getItem(storageKey(SIZE_KEY, agentKey));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.width === 'number' && typeof parsed.height === 'number') return clampSize(parsed);
    }
  } catch {
    /* ignore */
  }
  return clampSize({ width: 400, height: 560 });
}

let messageSeq = 0;
const nextId = () => `m${Date.now()}_${messageSeq++}`;

export const AgentChatWidget: React.FC<AgentChatWidgetProps> = ({
  agentKey = 'rds',
  selection,
  scope,
  scopeReady,
  selectionName,
  briefing = EMPTY_BRIEFING,
  welcomeCard,
  onActions,
  onConfirmProposal,
  handoff,
}) => {
  const [open, setOpen] = useState<boolean>(
    () => localStorage.getItem(storageKey(OPEN_KEY, agentKey)) === '1',
  );
  const [size, setSize] = useState(() => loadSize(agentKey));
  // On a phone a floating panel covers most of the screen anyway, and its resize corner is unusable.
  const fullScreen = useMediaQuery(PHONE_QUERY);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // Agent-offered follow-up chips for the latest assistant turn (cleared when a new turn starts).
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const resizeState = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);
  // Active chat-thread id, sent with every turn and rotated on reset (long-term archival boundary).
  const conversationIdRef = useRef<string>(loadConversationId(agentKey));
  // Proposals whose write is in flight. A ref because it must be claimed SYNCHRONOUSLY -- state
  // would not be visible to a second click landing in the same render pass.
  const confirmingRef = useRef<Set<number>>(new Set());

  // Persist open + size.
  useEffect(() => {
    localStorage.setItem(storageKey(OPEN_KEY, agentKey), open ? '1' : '0');
  }, [open, agentKey]);
  useEffect(() => {
    localStorage.setItem(storageKey(SIZE_KEY, agentKey), JSON.stringify(size));
  }, [size, agentKey]);

  // Auto-grow the input: reset to a single row, then expand to fit content up to the CSS max-height
  // (120px). Past that the textarea's own overflow-y takes over. Runs whenever the text changes,
  // including programmatic clears after send.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight excludes borders; with box-sizing: border-box we must add them back or the
    // content stays ~2px short and a phantom scrollbar appears.
    const cs = window.getComputedStyle(el);
    const borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    el.style.height = `${el.scrollHeight + borders}px`;
  }, [input]);

  // Keep the widget inside the viewport when it changes (browser zoom and window resize both fire
  // 'resize'). Without this, a size persisted at a larger viewport leaves the top-left resize handle
  // above the top edge, making the panel impossible to grab/shrink. Also clamps once on mount.
  useEffect(() => {
    const onWindowResize = () => setSize((prev) => clampSize(prev));
    window.addEventListener('resize', onWindowResize);
    setSize((prev) => clampSize(prev));
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  // Auto-scroll to bottom on new content.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Cancel any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  // The handoff turn ran under whatever thread id is stored now; adopt it so the next turn continues it.
  useEffect(() => {
    if (!handoff) return;
    conversationIdRef.current = loadConversationId(agentKey);
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'user', text: handoff.prompt },
      { id: nextId(), role: 'assistant', text: handoff.reply },
    ]);
    setSuggestions([]);
    setProposals([]);
    setOpen(true);
  }, [handoff, agentKey]);

  // ----- Resize (top-left handle grows the widget anchored bottom-right) -----
  // Pointer events, so a finger on a tablet can drag the corner as well as a mouse.
  const onResizeMove = useCallback((e: PointerEvent) => {
    const s = resizeState.current;
    if (!s) return;
    const dx = s.startX - e.clientX;
    const dy = s.startY - e.clientY;
    const maxW = Math.min(window.innerWidth - 40, 720);
    const maxH = Math.min(window.innerHeight - 40, 900);
    setSize({
      width: Math.max(MIN_W, Math.min(maxW, s.startW + dx)),
      height: Math.max(MIN_H, Math.min(maxH, s.startH + dy)),
    });
  }, []);

  const onResizeEnd = useCallback(() => {
    resizeState.current = null;
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
    window.removeEventListener('pointercancel', onResizeEnd);
    document.body.style.userSelect = '';
  }, [onResizeMove]);

  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      resizeState.current = { startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height };
      window.addEventListener('pointermove', onResizeMove);
      window.addEventListener('pointerup', onResizeEnd);
      window.addEventListener('pointercancel', onResizeEnd);
      document.body.style.userSelect = 'none';
    },
    [size.width, size.height, onResizeMove, onResizeEnd],
  );

  const runTurn = useCallback(
    async (messageText: string, opts?: { showUser?: boolean }) => {
      const text = messageText.trim();
      if (!text || busy) return;
      const showUser = opts?.showUser ?? true;

      const firstAsstId = nextId();
      const seed: ChatMessage[] = [];
      if (showUser) seed.push({ id: nextId(), role: 'user', text });
      seed.push({ id: firstAsstId, role: 'assistant', text: '', streaming: true });
      setMessages((prev) => [...prev, ...seed]);
      setSuggestions([]);
      // A new question makes the last turn's offers stale, INCLUDING ones already confirmed --
      // leaving those on screen invites a second click that would add the change twice.
      setProposals([]);
      confirmingRef.current = new Set();
      setBusy(true);

      const controller = new AbortController();
      abortRef.current = controller;

      // Idle watchdog: if the stream stalls (no real events) past the timeout, abort and surface a
      // graceful message. Reset on every event so an actively-streaming turn is never cut off.
      let timedOut = false;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      const clearIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = null;
      };
      const armIdle = () => {
        clearIdle();
        idleTimer = setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, TURN_IDLE_TIMEOUT_MS);
      };
      armIdle();

      // Each tool round-trip starts a fresh assistant bubble so iterative output
      // reads as separate messages instead of editing the original.
      let curAsstId: string | null = firstAsstId;
      let acc = '';
      // Server-issued id for this turn, delivered in the terminal `done` event; attached to every
      // assistant bubble this turn produced so each response stays rateable (not just the last one).
      let turnId: string | undefined;
      // Every assistant bubble id created during this turn. The turn's turnId is stamped onto all of
      // them on completion, so past responses keep their good/bad controls even after newer turns
      // arrive (and even when a turn ends on a tool step with no trailing text bubble).
      const turnAsstIds: string[] = [firstAsstId];

      const patch = (id: string, p: Partial<ChatMessage>) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...p } : m)));

      // Resolve any still-spinning tool chips (the step has completed).
      const settleToolChips = () =>
        setMessages((prev) =>
          prev.map((m) => (m.role === 'tool' && !m.done ? { ...m, done: true } : m)),
        );

      const ensureAssistant = () => {
        if (curAsstId === null) {
          curAsstId = nextId();
          acc = '';
          const id = curAsstId;
          turnAsstIds.push(id);
          setMessages((prev) => [...prev, { id, role: 'assistant', text: '', streaming: true }]);
        }
        return curAsstId;
      };

      // An error used to overwrite the bubble it landed in, wiping an answer that had already
      // streamed. Close that bubble as-is so the error gets one of its own.
      const keepStreamedText = () => {
        if (curAsstId === null || !acc.trim()) return;
        const keptId = curAsstId;
        setMessages((prev) => prev.map((m) => (m.id === keptId ? { ...m, streaming: false } : m)));
        curAsstId = null;
      };

      // Same story as the scope below: this named `seller_id` and `vin` and so dropped Sales
      // View's `client_id`, which is the client the rep has OPEN. Forward what the caller sent.
      const cleanSelection: AgentSelection = {};
      for (const [key, value] of Object.entries(selection)) {
        if (value) (cleanSelection as Record<string, unknown>)[key] = value;
      }

      // Forward the scope the CALLER built, minus empties. This was a hand-written copy of the
      // Client View keys, so every Sales View filter was dropped on the floor: a rep filtered to
      // one CSE clicked the "Health slipping" chip and got the whole book's 563 back, top-ranked
      // by someone else's territory (measured 2026-09-01). `AgentScope` already declared the
      // Sales keys — only this copier was left behind, so it no longer names keys at all.
      const cleanScope: AgentScope = {};
      for (const [key, value] of Object.entries(scope)) {
        const keep = Array.isArray(value) ? value.length > 0 : Boolean(value);
        if (keep) (cleanScope as Record<string, unknown>)[key] = value;
      }

      try {
        await siftApiClient.streamAgentChat(
          {
            message: text,
            selection: cleanSelection,
            scope: cleanScope,
            conversation_id: conversationIdRef.current,
            agent_key: agentKey,
          },
          (event: AgentStreamEvent) => {
            armIdle(); // a real event arrived — keep the turn alive
            if (event.type === 'delta') {
              // First token after a tool step: the lookup is done — settle chips.
              if (curAsstId === null) settleToolChips();
              const id = ensureAssistant();
              acc += event.text;
              patch(id, { text: acc });
            } else if (event.type === 'retract') {
              if (curAsstId !== null) {
                acc = '';
                patch(curAsstId, { text: '' });
              }
            } else if (event.type === 'tool') {
              // Finalize the current bubble (drop it if it never produced text),
              // show a status chip, then let the next delta open a new bubble.
              const finishingId = curAsstId;
              const hadText = acc.trim().length > 0;
              curAsstId = null;
              acc = '';
              setMessages((prev) => {
                const next = finishingId && !hadText
                  ? prev.filter((m) => m.id !== finishingId)
                  : prev.map((m) => (m.id === finishingId ? { ...m, streaming: false } : m));
                return [
                  ...next,
                  { id: nextId(), role: 'tool', text: '', tools: event.names ?? [] },
                ];
              });
            } else if (event.type === 'actions') {
              const acts = event.actions ?? [];
              // `suggest` directives become clickable follow-up chips in the widget, and
              // `propose_plan_edit` becomes a confirm button; everything else drives the dashboard UI.
              const sugg = acts
                .filter((a) => a.type === 'suggest' && a.label && a.prompt)
                .map((a) => ({ label: a.label as string, prompt: a.prompt as string }));
              const props = onConfirmProposal
                ? acts.filter((a) => a.type === 'propose_plan_edit')
                : [];
              const nav = acts.filter(
                (a) => a.type !== 'suggest' && a.type !== 'propose_plan_edit',
              );
              if (nav.length) onActions(nav);
              if (sugg.length) setSuggestions(sugg);
              if (props.length) {
                setProposals(props.map((action) => ({ action, state: 'offered' as const })));
              }
            } else if (event.type === 'error') {
              settleToolChips();
              keepStreamedText();
              const id = ensureAssistant();
              patch(id, { text: event.message, error: true, retryPrompt: text });
            } else if (event.type === 'done') {
              turnId = event.turn_id;
            }
          },
          controller.signal,
        );
        settleToolChips();
        if (curAsstId) patch(curAsstId, { streaming: false });
        // Stamp the turn's id onto every assistant bubble it produced (those still present — empty
        // pre-tool bubbles were dropped). This keeps each past response rateable, not just the last.
        if (turnId) {
          const tid = turnId;
          const ids = new Set(turnAsstIds);
          setMessages((prev) =>
            prev.map((m) =>
              ids.has(m.id) && m.role === 'assistant' && !m.error
                ? { ...m, turnId: tid, streaming: false }
                : m,
            ),
          );
        }
      } catch (err) {
        settleToolChips();
        const id = ensureAssistant();
        if (timedOut) {
          patch(id, {
            text:
              acc.trim()
                ? `${acc}\n\n_(Stopped — this was taking longer than expected. Try narrowing to a single dealer or VIN.)_`
                : 'That took longer than expected, so I stopped. Try narrowing the question to a single dealer or VIN and ask again.',
            error: !acc.trim(),
            streaming: false,
          });
        } else if (controller.signal.aborted) {
          patch(id, { streaming: false });
        } else {
          const msg = err instanceof Error ? err.message : 'The assistant is unavailable right now.';
          keepStreamedText();
          patch(ensureAssistant(), { text: msg, error: true, streaming: false, retryPrompt: text });
        }
      } finally {
        clearIdle();
        setBusy(false);
        abortRef.current = null;
      }
    },
    [busy, selection, scope, onActions, onConfirmProposal, agentKey],
  );

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    void runTurn(text, { showUser: true });
  }, [input, busy, runTurn]);

  // Click a dealer/VIN the agent named in its answer: steer the board straight to it.
  const handleEntityClick = useCallback(
    (action: AgentAction) => {
      onActions([action]);
    },
    [onActions],
  );

  // Click a follow-up chip or welcome-card quick-start: send its prompt as a normal turn.
  // Retrying a failed turn passes showUser: false — the original user bubble is still on screen.
  const handleSuggestion = useCallback(
    (prompt: string, opts?: { showUser?: boolean }) => {
      if (busy) return;
      void runTurn(prompt, { showUser: opts?.showUser ?? true });
    },
    [busy, runTurn],
  );

  // The rep pressed Confirm on one of Mia's offers. This is the ONLY path by which anything the
  // agent proposed reaches the plan.
  const handleConfirmProposal = useCallback(
    async (index: number) => {
      if (!onConfirmProposal) return;
      // Claimed through a REF, not through `proposals`: two clicks in one render pass share this
      // closure, so both would read state 'offered' and both would write. `disabled` does not
      // help either -- it only applies once React has re-rendered.
      if (confirmingRef.current.has(index)) return;
      const current = proposals[index];
      if (!current) return;
      confirmingRef.current.add(index);

      const mark = (next: Partial<Proposal>) =>
        setProposals((prev) => prev.map((p, i) => (i === index ? { ...p, ...next } : p)));

      mark({ state: 'saving', error: undefined });
      try {
        await onConfirmProposal(current.action, conversationIdRef.current);
        mark({ state: 'done' });
      } catch (err) {
        mark({
          state: 'failed',
          error: err instanceof Error && err.message ? err.message : 'Could not save that change.',
        });
      } finally {
        // Released so a failed offer can be retried; a successful one is now 'done' and the
        // button is gone.
        confirmingRef.current.delete(index);
      }
    },
    [onConfirmProposal, proposals],
  );

  const handleReset = useCallback(async () => {
    abortRef.current?.abort();
    setMessages([]);
    setSuggestions([]);
    // Archive the conversation we're leaving, then rotate to a fresh thread id.
    const endingId = conversationIdRef.current;
    const nextConvId = newConversationId();
    conversationIdRef.current = nextConvId;
    saveConversationId(agentKey, nextConvId);
    try {
      await siftApiClient.resetAgentChat(endingId);
    } catch {
      /* best effort */
    }
  }, [agentKey]);

  // The conversation deliberately PERSISTS across selection changes (dealer focus, VIN open/close,
  // or clearing to no selection). The current selection is re-injected into the prompt every turn
  // and the tools honor whichever dealer/VIN the user names, so the thread can follow the user
  // around the board — including "now compare this to dealer B" — without losing context. Starting a
  // fresh thread is an explicit user action via the Reset button (handleReset).

  // Passive good/bad rating on one response. Clicking the active rating again clears it. Optimistic
  // local update; the backend call is best-effort (no UI failure if it doesn't land).
  const handleFeedback = useCallback(
    (msgId: string, turnId: string, rating: AgentFeedbackRating) => {
      let next: AgentFeedbackRating | null = rating;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== msgId) return m;
          next = m.feedback === rating ? null : rating;
          return { ...m, feedback: next };
        }),
      );
      void siftApiClient
        .sendAgentFeedback(conversationIdRef.current, turnId, next)
        .catch(() => {
          /* best effort */
        });
    },
    [],
  );


  const onInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const selectionLabel = selectionName
    ? selectionName
    : selection.vin
      ? `VIN ${selection.vin}`
      : selection.seller_id
        ? `Dealer ${selection.seller_id}`
        : selection.client_id
          ? `Client ${selection.client_id}`
          : 'No selection';

  // Everything Mia is currently scoped to, shown the same way the dealer/VIN focus is — the rep
  // can widen it by asking her, so this is a status line rather than a control.
  const scopeLabels: string[] = [];
  for (const [key, value] of Object.entries(scope)) {
    if (key === 'metric_filters' || key === 'inspector_zip' || key === 'inspector_seller_id'
        || key === 'radius_miles') {
      continue; // handled below, they do not read as "Label: value"
    }
    const label = SCOPE_TAG_LABELS[key] ?? key.replace(/_/g, ' ');
    if (Array.isArray(value)) {
      if (!value.length) continue;
      const shown = value.slice(0, 3).join(', ');
      scopeLabels.push(`${label}: ${shown}${value.length > 3 ? ` +${value.length - 3}` : ''}`);
    } else if (typeof value === 'boolean') {
      if (value) scopeLabels.push(label);
    } else if (value) {
      scopeLabels.push(`${label}: ${value}`);
    }
  }
  if (scope.metric_filters) {
    const n = Object.keys(scope.metric_filters).length;
    scopeLabels.push(`${n} metric filter${n === 1 ? '' : 's'}`);
  }
  if (scope.inspector_zip || scope.inspector_seller_id) {
    scopeLabels.push(`Within ${scope.radius_miles ?? 50} mi`);
  }

  const briefingReady = scopeReady && briefing.ready;

  if (!open) {
    return (
      <button
        className="agent-fab"
        onClick={() => setOpen(true)}
        aria-label="Open Mia assistant"
        title="Ask Mia"
      >
        <span className="agent-fab-icon">✦</span>
        <span className="agent-fab-label">Ask Mia</span>
      </button>
    );
  }

  return (
    <div
      data-usage-section="mia"
      className={`agent-widget${fullScreen ? ' agent-widget--full' : ''}`}
      style={fullScreen ? undefined : { width: size.width, height: size.height }}
      role={fullScreen ? 'dialog' : undefined}
      aria-modal={fullScreen || undefined}
      aria-label={fullScreen ? 'Mia assistant' : undefined}
    >
      {!fullScreen && (
        <div className="agent-resize-handle" onPointerDown={onResizeStart} aria-hidden="true" />
      )}

      <div className="agent-header">
        <div className="agent-title">
          <span className="agent-title-mark">✦</span> Mia
        </div>
        <div className="agent-header-actions">
          <button className="agent-icon-btn" onClick={handleReset} title="New conversation" aria-label="Reset conversation">
            ⟳
          </button>
          <button className="agent-icon-btn" onClick={() => setOpen(false)} title={fullScreen ? 'Close' : 'Minimize'} aria-label={fullScreen ? 'Close Mia' : 'Minimize'}>
            {fullScreen ? '✕' : '—'}
          </button>
        </div>
      </div>

      <div className="agent-selection-banner" title="Mia scopes answers to this focus and your active board filters. Ask her to widen it.">
        <span className="agent-selection-dot" /> {selectionLabel}
        {scopeLabels.map((s) => (
          <span key={s} className="agent-scope-tag">{s}</span>
        ))}
      </div>

      <div className="agent-messages" ref={listRef}>
        {messages.length === 0 && welcomeCard ? (
          <div className="agent-welcome">{welcomeCard(handleSuggestion, busy)}</div>
        ) : messages.length === 0 ? (
          <div className="agent-welcome">
            <div className="agent-welcome-greeting">
              <span className="agent-welcome-mark">✦</span>
              <div>
                <div className="agent-welcome-title">Welcome to Mia</div>
                <div className="agent-welcome-sub">
                  {!briefingReady
                    ? 'Standby while I pull the latest insights for you…'
                    : briefing.selectedVehicle
                      ? `Focused on ${briefing.selectedVehicle.label} — here's where to start.`
                      : briefing.selectedDealer
                        ? `Focused on ${briefing.selectedDealer.name} — here's where to start.`
                        : `${briefing.dealerCount} dealer${briefing.dealerCount === 1 ? '' : 's'} in your current view — pick where you'd like to start.`}
                </div>
              </div>
            </div>

            {!briefingReady && (
              <div className="agent-welcome-loading">
                <span className="agent-tool-spinner" aria-hidden="true" /> Gathering your board…
              </div>
            )}

            {/* Vehicle-scoped starting points (a VIN pop-out is open). */}
            {briefingReady && briefing.selectedVehicle && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">🚗 This vehicle</div>
                <div className="agent-chip-wrap">
                  {presetPrompts('vehicle', {
                    vehicle: briefing.selectedVehicle.label,
                    vin: briefing.selectedVehicle.vin,
                  }).map((s) => (
                    <button
                      key={s.label}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() => handleSuggestion(s.prompt)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dealer-scoped starting points (a dealer is focused, no VIN pop-out). */}
            {briefingReady && !briefing.selectedVehicle && briefing.selectedDealer && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">🏬 This dealer</div>
                <div className="agent-chip-wrap">
                  {presetPrompts('dealer', { dealer: briefing.selectedDealer.name }).map((s) => (
                    <button
                      key={s.label}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() => handleSuggestion(s.prompt)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.dealerCount === 0 && (
              <div className="agent-welcome-empty">
                No dealers in your current view. Adjust the board filters, or ask me anything below.
              </div>
            )}

            {/* Inspection cadence / call list. Shown as counted bands like priority and tier so all
                three lenses read the same way and the rep can see the size of each before clicking. */}
            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.dealerCount > 0 && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">📞 Inspections &amp; cadence</div>
                <div className="agent-chip-wrap">
                  {briefing.callListBands.map((b) => (
                    <button
                      key={b.key}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() =>
                        handleSuggestion(presetPrompts('callListBand', { band: b.label })[0].prompt)
                      }
                    >
                      {b.label}
                      <span className="agent-quick-count">{b.clients}</span>
                    </button>
                  ))}
                  {presetPrompts('callList').map((s) => (
                    <button
                      key={s.label}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() => handleSuggestion(s.prompt)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.dealerCount > 0 && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">💲 Pricing against MMR</div>
                <div className="agent-chip-wrap">
                  {presetPrompts('pricing').map((s) => (
                    <button
                      key={s.label}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() => handleSuggestion(s.prompt)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.priorities.length > 0 && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">🔥 By priority</div>
                <div className="agent-chip-wrap">
                  {briefing.priorities.map((p) => (
                    <button
                      key={p.key}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() =>
                        handleSuggestion(
                          `Show me the ${p.label.toLowerCase()} units I should prioritize across my dealers.`,
                        )
                      }
                    >
                      {p.label}
                      <span className="agent-quick-count">{p.units}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.tiers.length > 0 && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">⭐ By dealer tier</div>
                <div className="agent-chip-wrap">
                  {briefing.tiers.map((t) => (
                    <button
                      key={t.key}
                      className="agent-quick-chip"
                      disabled={busy}
                      onClick={() =>
                        handleSuggestion(`Which ${t.label} dealers need my attention right now, and why?`)
                      }
                    >
                      {t.label}
                      <span className="agent-quick-count">{t.dealers}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && !briefing.selectedDealer && !briefing.selectedVehicle && briefing.topDealers.length > 0 && (
              <div className="agent-welcome-section">
                <div className="agent-welcome-label">Top dealers to start with</div>
                <div className="agent-chip-wrap agent-chip-wrap-col">
                  {briefing.topDealers.map((d) => (
                    <button
                      key={d.sellerId}
                      className="agent-quick-dealer"
                      disabled={busy}
                      onClick={() =>
                        handleSuggestion(`Give me the rundown on ${d.name} and what I should do next.`)
                      }
                    >
                      <span className="agent-quick-dealer-name">{d.name}</span>
                      <span className="agent-quick-dealer-meta">
                        {d.count} {d.bucketLabel}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {briefingReady && <div className="agent-welcome-hint">Or ask me anything below.</div>}
          </div>
        ) : (
          messages.map((m) => {
            if (m.role === 'tool') {
              const label = (m.tools && m.tools.length ? m.tools.map(toolLabel) : ['Working']).join(' · ');
              return (
                <div key={m.id} className={`agent-tool-chip${m.done ? ' agent-tool-chip-done' : ''}`}>
                  {m.done ? (
                    <span className="agent-tool-check" aria-hidden="true">✓</span>
                  ) : (
                    <span className="agent-tool-spinner" aria-hidden="true" />
                  )}
                  {label}
                </div>
              );
            }
            return (
              <div key={m.id} className={`agent-msg agent-msg-${m.role}${m.error ? ' agent-msg-error' : ''}`}>
                <div className="agent-msg-bubble">
                  {m.role === 'assistant' && m.text ? (
                    <AssistantMessage
                      text={m.text}
                      streaming={m.streaming}
                      onEntityClick={handleEntityClick}
                    />
                  ) : (
                    m.text
                  )}
                  {m.streaming && !m.text && (
                    <span className="agent-stream-cursor" aria-hidden="true">
                      <i />
                    </span>
                  )}
                </div>
                {m.role === 'assistant' && !m.streaming && !m.error && m.turnId && (
                  <FeedbackButtons
                    value={m.feedback}
                    onRate={(rating) => handleFeedback(m.id, m.turnId as string, rating)}
                  />
                )}
                {m.error && m.retryPrompt && !busy && (
                  <button
                    className="agent-retry-btn"
                    onClick={() => handleSuggestion(m.retryPrompt as string, { showUser: false })}
                  >
                    ⟳ Try again
                  </button>
                )}
              </div>
            );
          })
        )}

        {proposals.length > 0 && !busy && (
          <div className="agent-proposals">
            {proposals.map((p, i) => (
              <div key={`${i}-${p.action.client_id}`} className={`agent-proposal is-${p.state}`}>
                <div className="agent-proposal-text">
                  <strong>{proposalLabel(p.action)}</strong>
                  {p.action.reason ? <span>{p.action.reason}</span> : null}
                  {p.state === 'failed' && p.error ? (
                    <span className="agent-proposal-error">{p.error}</span>
                  ) : null}
                </div>
                {p.state === 'done' ? (
                  <span className="agent-proposal-done">Added to your week</span>
                ) : (
                  <button
                    type="button"
                    className="agent-proposal-confirm"
                    disabled={p.state === 'saving'}
                    onClick={() => void handleConfirmProposal(i)}
                  >
                    {p.state === 'saving' ? 'Saving…' : p.state === 'failed' ? 'Retry' : 'Confirm'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {messages.length > 0 && suggestions.length > 0 && !busy && (
          <div className="agent-suggestions">
            {suggestions.map((s, i) => (
              <button
                key={`${i}-${s.label}`}
                className="agent-suggestion-chip"
                onClick={() => handleSuggestion(s.prompt)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="agent-input-row">
        <textarea
          className="agent-input"
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder="Ask Mia…"
          rows={1}
          disabled={busy}
        />
        <button className="agent-send-btn" onClick={handleSend} disabled={busy || !input.trim()} aria-label="Send">
          {busy ? '…' : '↑'}
        </button>
      </div>
    </div>
  );
};
